import { useEffect, useState, useContext } from 'react'
import { router } from '@inertiajs/react'
import styles from './ProjectDetails.module.css'
import CreateTaskModal from '../modals/CreateTaskModal'
import EditProjectModal from '../modals/EditProjectModal'
import AddUserToProjectModal from '../modals/AddUserToProjectModal'
import AddGroupToProjectModal from '../modals/AddGroupToProjectModal'
import apiClient from '../../utils/api'
import { formatDisplayDate } from '../../utils/dateUtils'
import { DataRefreshContext } from '../ManageSection'

export default function ProjectDetails({
    project: initialProject,
    onClose,
    apiToken,
    onSuccessMessage,
    onError,
    onProjectUpdate
}) {
    const [project, setProject] = useState(initialProject)
    const [clients, setClients] = useState([])
    const [showEditProjectModal, setShowEditProjectModal] = useState(false)
    const [showCreateTaskModal, setShowCreateTaskModal] = useState(false)
    const [showAddUserModal, setShowAddUserModal] = useState(false)
    const [showAddGroupModal, setShowAddGroupModal] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingDetails, setIsLoadingDetails] = useState(false)
    const [editingTask, setEditingTask] = useState(null)
    const [timeLockEnabled, setTimeLockEnabled] = useState(!!initialProject?.locked_before_date)
    const [lockedBeforeDate, setLockedBeforeDate] = useState(initialProject?.locked_before_date || '')
    const [lockFrequency, setLockFrequency] = useState(initialProject?.lock_frequency || 'manual')
    const [gracePeriodDays, setGracePeriodDays] = useState(initialProject?.grace_period_days || 0)
    const [lockDayOfWeek, setLockDayOfWeek] = useState(initialProject?.lock_day_of_week || 0)

    // Get data refresh context to trigger global refreshes
    const dataRefreshContext = useContext(DataRefreshContext)
    const { triggerRefresh } = dataRefreshContext || {}

    // Update local state when prop changes
    useEffect(() => {
        setProject(initialProject)
        syncTimeLockState(initialProject)
    }, [initialProject])

    const syncTimeLockState = (data) => {
        setTimeLockEnabled(!!data?.locked_before_date)
        setLockedBeforeDate(data?.locked_before_date || '')
        setLockFrequency(data?.lock_frequency || 'manual')
        setGracePeriodDays(data?.grace_period_days || 0)
        setLockDayOfWeek(data?.lock_day_of_week || 0)
    }

    // Load project details and clients when component mounts or project ID changes
    useEffect(() => {
        if (project?.id) {
            loadProjectDetails()
            loadClients()
        }
    }, [project?.id])

    const loadProjectDetails = async () => {
        if (!project?.id) return

        setIsLoadingDetails(true)
        try {
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.getProject(project.id)

            if (response.project) {
                setProject(response.project)
                syncTimeLockState(response.project)
                // Also notify parent to update its state
                if (onProjectUpdate) {
                    onProjectUpdate(response.project)
                }
            }
        } catch (err) {
            console.error('Error loading project details:', err)
        } finally {
            setIsLoadingDetails(false)
        }
    }

    const loadClients = async () => {
        try {
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.getClients()

            if (response.clients) {
                setClients(response.clients)
            }
        } catch (err) {
            console.error('Error loading clients:', err)
        }
    }

    if (!project) return null

    const formatDate = (dateString) => {
        return formatDisplayDate(dateString)
    }

    const getStatusBadge = (status) => {
        const statusClass = {
            'active': styles.statusActive,
            'inactive': styles.statusInactive,
            'archived': styles.statusArchived
        }[status] || styles.statusActive

        const statusText = {
            'active': 'Active',
            'inactive': 'Inactive',
            'archived': 'Archived'
        }[status] || status

        return (
            <span className={`${styles.statusBadge} ${statusClass}`}>
                {statusText}
            </span>
        )
    }

    const handleTaskSaved = (savedTask) => {
        const isEdit = !!editingTask
        const message = isEdit
            ? `Task "${savedTask.name}" updated successfully`
            : `Task "${savedTask.name}" created successfully`

        if (onSuccessMessage) {
            onSuccessMessage(message)
        }
        loadProjectDetails() // Refresh to show updated tasks
        setEditingTask(null) // Clear editing state
    }

    const handleProjectUpdated = (updatedProject, message) => {
        setProject(updatedProject)
        if (onProjectUpdate) {
            onProjectUpdate(updatedProject)
        }
        if (onSuccessMessage && message) {
            onSuccessMessage(message)
        }
        loadProjectDetails() // Refresh project details
    }

    const handleUserAdded = async (updatedData, message) => {
        if (onSuccessMessage && message) {
            onSuccessMessage(message)
        }
        // Refresh to show new user and ensure parent gets updated too
        await loadProjectDetails()
        // Trigger global refresh to update all manage tables
        if (triggerRefresh) {
            triggerRefresh('ProjectDetails.handleUserAdded')
        }
    }

    const handleGroupAdded = (updatedData, message) => {
        if (onSuccessMessage && message) {
            onSuccessMessage(message)
        }
        loadProjectDetails() // Refresh to show new group
        // Trigger global refresh to update all manage tables
        if (triggerRefresh) {
            triggerRefresh('ProjectDetails.handleGroupAdded')
        }
    }

    const handleRemoveUserAccess = async (userId) => {
        const user = project.users_with_access?.find(u => u.id === userId)
        if (!user) return

        const confirmRemoval = window.confirm(
            `Are you sure you want to remove ${user.full_name} from this project?`
        )
        if (!confirmRemoval) return

        try {
            setIsLoading(true)
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.removeUserFromProject(project.id, userId)

            if (response.success) {
                if (onSuccessMessage) {
                    onSuccessMessage(`${user.full_name} removed from project`)
                }
                loadProjectDetails() // Refresh access list
                // Trigger global refresh to update all manage tables
                if (triggerRefresh) {
                    triggerRefresh('ProjectDetails.handleRemoveUserAccess')
                }
            } else {
                if (onError) {
                    onError(response.error || 'Failed to remove user from project')
                }
            }
        } catch (err) {
            console.error('Error removing user:', err)
            if (onError) {
                onError('Failed to remove user from project')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleRemoveGroupAccess = async (groupId) => {
        const group = project.groups_with_access?.find(g => g.id === groupId)
        if (!group) return

        const confirmRemoval = window.confirm(
            `Are you sure you want to remove the group "${group.name}" from this project? All group members will lose access.`
        )
        if (!confirmRemoval) return

        try {
            setIsLoading(true)
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.removeProjectFromGroup(groupId, project.id)

            if (response.success) {
                if (onSuccessMessage) {
                    onSuccessMessage(`Group "${group.name}" removed from project`)
                }
                loadProjectDetails() // Refresh access list
            } else {
                if (onError) {
                    onError(response.error || 'Failed to remove group from project')
                }
            }
        } catch (err) {
            console.error('Error removing group:', err)
            if (onError) {
                onError('Failed to remove group from project')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleArchiveProject = async () => {
        const confirmArchive = window.confirm(
            `Are you sure you want to archive "${project.name}"? This will make it inactive but preserve all data.`
        )
        if (!confirmArchive) return

        try {
            setIsLoading(true)
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.updateProject(project.id, {
                project: { status: 'archived' }
            })

            if (response.success || response.project) {
                const updatedProject = response.project || { ...project, status: 'archived' }
                handleProjectUpdated(updatedProject, 'Project archived successfully')
            } else {
                if (onError) {
                    onError(response.error || 'Failed to archive project')
                }
            }
        } catch (err) {
            console.error('Error archiving project:', err)
            if (onError) {
                onError('Failed to archive project')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleViewTimelog = () => {
        router.visit(`/reports/detailed?project=${project.id}`)
    }

    const handleEditTask = (task) => {
        setEditingTask(task)
        setShowCreateTaskModal(true)
    }

    const handleDeleteTask = async (task) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete the task "${task.name}"? This action cannot be undone.`
        )
        if (!confirmDelete) return

        try {
            setIsLoading(true)
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.deleteTask(project.id, task.id)

            if (response.success) {
                if (onSuccessMessage) {
                    onSuccessMessage(`Task "${task.name}" deleted successfully`)
                }
                loadProjectDetails() // Refresh to show updated tasks
            } else {
                if (onError) {
                    onError(response.error || 'Failed to delete task')
                }
            }
        } catch (err) {
            console.error('Error deleting task:', err)
            if (onError) {
                onError('Failed to delete task')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const handleTimeLockSave = async () => {
        if (timeLockEnabled && !lockedBeforeDate) {
            onError?.('Please select a lock date')
            return
        }

        // Validate grace period
        if (lockFrequency !== 'manual' && gracePeriodDays < 0) {
            onError?.('Grace period must be 0 or greater')
            return
        }

        try {
            setIsLoading(true)
            apiClient.setAuthToken(apiToken)
            await apiClient.updateProjectTimeLock(project.id, {
                lock_enabled: timeLockEnabled,
                locked_before_date: lockedBeforeDate,
                lock_frequency: lockFrequency,
                grace_period_days: gracePeriodDays,
                lock_day_of_week: lockDayOfWeek
            })
            if (onSuccessMessage) {
                const frequencyText = lockFrequency === 'manual' ? 'Manual' : lockFrequency === 'weekly' ? 'Weekly' : 'Monthly'
                const message = timeLockEnabled
                    ? `Lock saved: ${frequencyText} lock through ${formatDate(lockedBeforeDate)}`
                    : 'Time lock disabled'
                onSuccessMessage(message)
            }
            await loadProjectDetails()
        } catch (err) {
            console.error('Error updating time lock:', err)
            onError?.(err.message || 'Failed to update time lock')
        } finally {
            setIsLoading(false)
        }
    }

    const usersWithAccess = project.users_with_access || []
    const groupsWithAccess = project.groups_with_access || []

    return (
        <div className={styles.detailsPanel}>
            <div className={styles.detailsHeader}>
                <div className={styles.headerInfo}>
                    <h2>{project.name}</h2>
                    <div className={styles.headerMeta}>
                        {getStatusBadge(project.status)}
                        {project.client_name && (
                            <span className={styles.clientBadge}>
                                {project.client_name}
                            </span>
                        )}
                    </div>
                </div>
                <button className={styles.closeButton} onClick={onClose}>×</button>
            </div>

            <div className={styles.detailsContent}>
                {isLoadingDetails && (
                    <div className={styles.loadingMessage}>
                        Loading project details...
                    </div>
                )}

                <div className={styles.detailSection}>
                    <h3>Project Information</h3>
                    <div className={styles.detailGrid}>
                        <div className={styles.detailItem}>
                            <label>Status</label>
                            {getStatusBadge(project.status)}
                        </div>
                        {project.client_name && (
                            <div className={styles.detailItem}>
                                <label>Client</label>
                                <span>{project.client_name}</span>
                            </div>
                        )}
                        <div className={styles.detailItem}>
                            <label>Tasks</label>
                            <span>{project.tasks_count || 0}</span>
                        </div>
                        <div className={styles.detailItem}>
                            <label>Total Hours</label>
                            <span>{Math.floor((project.total_logged_hours || 0) / 3600)}h</span>
                        </div>
                        {project.created_at && (
                            <div className={styles.detailItem}>
                                <label>Created</label>
                                <span>{formatDate(project.created_at)}</span>
                            </div>
                        )}
                        {project.updated_at && (
                            <div className={styles.detailItem}>
                                <label>Last Updated</label>
                                <span>{formatDate(project.updated_at)}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.detailSection}>
                    <h3>Description</h3>
                    <p>{project.description || 'No description available'}</p>
                </div>

                <div className={styles.detailSection}>
                    <h3>Time Lock</h3>
                    <div className={styles.timeLockControls}>
                        <label className={styles.toggleLabel}>
                            <input
                                type="checkbox"
                                checked={timeLockEnabled}
                                onChange={(e) => setTimeLockEnabled(e.target.checked)}
                            />
                            Enable time lock
                        </label>
                        <div className={styles.timeLockRow}>
                            <div className={styles.dateInputGroup}>
                                <label>Lock entries through</label>
                                <input
                                    type="date"
                                    value={lockedBeforeDate || ''}
                                    onChange={(e) => setLockedBeforeDate(e.target.value)}
                                    disabled={!timeLockEnabled}
                                />
                            </div>
                            <div className={styles.dateInputGroup}>
                                <label>Lock Type</label>
                                <select
                                    value={lockFrequency}
                                    onChange={(e) => setLockFrequency(e.target.value)}
                                    disabled={!timeLockEnabled}
                                    className={styles.selectInput}
                                >
                                    <option value="manual">Manual</option>
                                    <option value="weekly">Weekly - Auto-lock each week</option>
                                    <option value="monthly">Monthly - Auto-lock each month</option>
                                </select>
                            </div>
                        </div>
                        {timeLockEnabled && lockFrequency !== 'manual' && (
                            <div className={styles.timeLockRow}>
                                <div className={styles.dateInputGroup}>
                                    <label>Grace Period (days)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={gracePeriodDays}
                                        onChange={(e) => setGracePeriodDays(parseInt(e.target.value) || 0)}
                                        disabled={!timeLockEnabled}
                                    />
                                </div>
                                {lockFrequency === 'weekly' && (
                                    <div className={styles.dateInputGroup}>
                                        <label>Week Ends On</label>
                                        <select
                                            value={lockDayOfWeek}
                                            onChange={(e) => setLockDayOfWeek(parseInt(e.target.value))}
                                            disabled={!timeLockEnabled}
                                            className={styles.selectInput}
                                        >
                                            <option value="0">Sunday</option>
                                            <option value="1">Monday</option>
                                            <option value="2">Tuesday</option>
                                            <option value="3">Wednesday</option>
                                            <option value="4">Thursday</option>
                                            <option value="5">Friday</option>
                                            <option value="6">Saturday</option>
                                        </select>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={styles.timeLockRow}>
                            <button
                                className={styles.primaryButton}
                                onClick={handleTimeLockSave}
                                disabled={isLoading || (timeLockEnabled && !lockedBeforeDate)}
                            >
                                {isLoading ? 'Saving...' : 'Save Lock'}
                            </button>
                        </div>
                    </div>
                    {project.locked_before_date && (
                        <p className={styles.timeLockStatus}>
                            Currently locked through <strong>{formatDate(project.locked_before_date)}</strong>
                            {project.lock_frequency && project.lock_frequency !== 'manual' && (
                                <span> • Automation: <strong>{project.lock_frequency}</strong></span>
                            )}
                        </p>
                    )}
                </div>

                {/* Users with Access Section */}
                <div className={styles.detailSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Users with Access ({usersWithAccess.length})</h3>
                        <button
                            className={styles.addButton}
                            onClick={() => setShowAddUserModal(true)}
                            disabled={isLoading}
                        >
                            Add User
                        </button>
                    </div>

                    {usersWithAccess.length === 0 ? (
                        <p className={styles.emptyMessage}>No users have direct access to this project</p>
                    ) : (
                        <div className={styles.accessList}>
                            {usersWithAccess.map(user => {
                                const isWorkspaceAdmin = user.granted_by === 'Workspace Admin'
                                return (
                                    <div key={user.id} className={styles.accessItem}>
                                        <div className={styles.accessInfo}>
                                            <strong>{user.full_name}</strong>
                                            <span>{user.email}</span>
                                            {isWorkspaceAdmin ? (
                                                <small>Workspace Admin</small>
                                            ) : (
                                                <small>Role: {user.role} • Added by {user.granted_by}</small>
                                            )}
                                        </div>
                                        {!isWorkspaceAdmin && (
                                            <button
                                                className={styles.removeButton}
                                                onClick={() => handleRemoveUserAccess(user.id)}
                                                disabled={isLoading}
                                                title="Remove user from project"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                {/* Groups with Access Section */}
                <div className={styles.detailSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Groups with Access ({groupsWithAccess.length})</h3>
                        <button
                            className={styles.addButton}
                            onClick={() => setShowAddGroupModal(true)}
                            disabled={isLoading}
                        >
                            Add Group
                        </button>
                    </div>

                    {groupsWithAccess.length === 0 ? (
                        <p className={styles.emptyMessage}>No groups have access to this project</p>
                    ) : (
                        <div className={styles.accessList}>
                            {groupsWithAccess.map(group => (
                                <div key={group.id} className={styles.accessItem}>
                                    <div className={styles.accessInfo}>
                                        <strong>{group.name}</strong>
                                        <span>{group.member_count} member{group.member_count !== 1 ? 's' : ''}</span>
                                        <small>Role: {group.role} • Added by {group.granted_by}</small>
                                        {group.description && (
                                            <small className={styles.groupDescription}>{group.description}</small>
                                        )}
                                    </div>
                                    <button
                                        className={styles.removeButton}
                                        onClick={() => handleRemoveGroupAccess(group.id)}
                                        disabled={isLoading}
                                        title="Remove group from project"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Billable Tasks Section */}
                <div className={styles.detailSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Billable Tasks ({(project.tasks || []).filter(t => t.is_billable).length})</h3>
                        <button
                            className={styles.addButton}
                            onClick={() => {
                                setEditingTask({ is_billable: true })
                                setShowCreateTaskModal(true)
                            }}
                            disabled={isLoading}
                        >
                            Create Billable Task
                        </button>
                    </div>

                    {(!project.tasks || project.tasks.filter(t => t.is_billable).length === 0) ? (
                        <p className={styles.emptyMessage}>No billable tasks created yet.</p>
                    ) : (
                        <div className={styles.taskList}>
                            {project.tasks.filter(t => t.is_billable).map(task => (
                                <div key={task.id} className={styles.taskItem}>
                                    <div className={styles.taskInfo}>
                                        <div className={styles.taskHeader}>
                                            <div
                                                className={styles.taskColorIndicator}
                                                style={{ backgroundColor: task.color }}
                                            ></div>
                                            <strong>{task.name}</strong>
                                        </div>
                                        <div className={styles.taskMeta}>
                                            <span
                                                className={`${styles.statusBadge} ${task.status === 'active' ? styles.statusActive : styles.statusInactive}`}>
                                                {task.status === 'active' ? 'Active' : 'Inactive'}
                                            </span>
                                            <span className={styles.taskHours}>
                                                {Math.floor((task.total_logged_hours || 0) / 3600)} hours logged
                                            </span>
                                            <span className={styles.taskDate}>
                                                Created {formatDate(task.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.taskActions}>
                                        <button
                                            className={styles.taskEditButton}
                                            onClick={() => handleEditTask(task)}
                                            disabled={isLoading}
                                            title="Edit task"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className={styles.taskDeleteButton}
                                            onClick={() => handleDeleteTask(task)}
                                            disabled={isLoading}
                                            title="Delete task"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Other Tasks (Non-Billable) Section */}
                <div className={styles.detailSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Other Tasks (Non-Billable) ({(project.tasks || []).filter(t => !t.is_billable).length})</h3>
                        <button
                            className={styles.addButton}
                            onClick={() => {
                                setEditingTask({ is_billable: false })
                                setShowCreateTaskModal(true)
                            }}
                            disabled={isLoading}
                        >
                            Create Non-Billable Task
                        </button>
                    </div>

                    {(!project.tasks || project.tasks.filter(t => !t.is_billable).length === 0) ? (
                        <p className={styles.emptyMessage}>No non-billable tasks created yet.</p>
                    ) : (
                        <div className={styles.taskList}>
                            {project.tasks.filter(t => !t.is_billable).map(task => (
                                <div key={task.id} className={styles.taskItem}>
                                    <div className={styles.taskInfo}>
                                        <div className={styles.taskHeader}>
                                            <div
                                                className={styles.taskColorIndicator}
                                                style={{ backgroundColor: task.color }}
                                            ></div>
                                            <strong>{task.name}</strong>
                                        </div>
                                        <div className={styles.taskMeta}>
                                            <span
                                                className={`${styles.statusBadge} ${task.status === 'active' ? styles.statusActive : styles.statusInactive}`}>
                                                {task.status === 'active' ? 'Active' : 'Inactive'}
                                            </span>
                                            <span className={styles.taskHours}>
                                                {Math.floor((task.total_logged_hours || 0) / 3600)} hours logged
                                            </span>
                                            <span className={styles.taskDate}>
                                                Created {formatDate(task.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.taskActions}>
                                        <button
                                            className={styles.taskEditButton}
                                            onClick={() => handleEditTask(task)}
                                            disabled={isLoading}
                                            title="Edit task"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className={styles.taskDeleteButton}
                                            onClick={() => handleDeleteTask(task)}
                                            disabled={isLoading}
                                            title="Delete task"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.detailSection}>
                    <h3>Actions</h3>
                    <div className={styles.detailActions}>
                        <button
                            className={styles.primaryButton}
                            onClick={() => setShowEditProjectModal(true)}
                            disabled={isLoading}
                        >
                            Edit Project
                        </button>
                        <button
                            className={styles.secondaryButton}
                            onClick={() => setShowCreateTaskModal(true)}
                            disabled={isLoading}
                        >
                            Manage Tasks
                        </button>
                        <button
                            className={styles.secondaryButton}
                            onClick={handleViewTimelog}
                        >
                            View Time Logs
                        </button>
                        {project.status !== 'archived' && (
                            <button
                                className={styles.dangerButton}
                                onClick={handleArchiveProject}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Archiving...' : 'Archive Project'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Edit Project Modal */}
            <EditProjectModal
                isOpen={showEditProjectModal}
                onClose={() => setShowEditProjectModal(false)}
                onSave={handleProjectUpdated}
                project={project}
                clients={clients}
                apiToken={apiToken}
            />

            {/* Create/Edit Task Modal */}
            <CreateTaskModal
                isOpen={showCreateTaskModal}
                onClose={() => {
                    setShowCreateTaskModal(false)
                    setEditingTask(null) // Clear editing state
                }}
                onSave={handleTaskSaved}
                project={project}
                task={editingTask} // null = create mode, object = edit mode
                apiToken={apiToken}
            />

            {/* Add User Modal */}
            <AddUserToProjectModal
                isOpen={showAddUserModal}
                onClose={() => setShowAddUserModal(false)}
                onSave={handleUserAdded}
                project={project}
                apiToken={apiToken}
            />

            {/* Add Group Modal */}
            <AddGroupToProjectModal
                isOpen={showAddGroupModal}
                onClose={() => setShowAddGroupModal(false)}
                onSave={handleGroupAdded}
                project={project}
                apiToken={apiToken}
            />
        </div>
    )
}
